import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, switchMap, takeWhile, withLatestFrom } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Observable, of, timer } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep } from 'lodash';
import { AppState } from '../../store';
import {
  clearHistory,
  closePackageImportModal,
  closePackageVersionMessageModal,
  createMajorVersion,
  createMajorVersionError,
  cutAction,
  exportJSON,
  exportJSONError,
  exportJSONSuccess,
  getValidationStatus,
  importJSON,
  importJSONError,
  openComponentsModal,
  removeComponents,
  runValidationAction,
  runValidationError,
  savePackage,
  savePackageError,
  savePackageSuccess,
  validationStatusError,
  validationStatusSuccess,
} from './package-designer.actions';
import { setComponent } from './component.actions';
import { findComponentById, fixComponentData, getDataFromComponent } from '../helpers/components.helpers';
import { PackagesResource } from '../../packages/resources/packages.resource';
import { NotifyService } from '../../common/services/notify.service';
import { setPackageFromJson } from './package.actions';
import { openJobsModal } from '../../jobs/store/jobs.actions';

function downloadObjectAsJson(exportObj: any, exportName: string) {
  const dataStr = `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(exportObj, null, 4))}`;
  const downloadAnchorNode = document.createElement('a');
  downloadAnchorNode.setAttribute('href', dataStr);
  downloadAnchorNode.setAttribute('download', `${exportName}.json`);
  document.body.appendChild(downloadAnchorNode); // required for firefox
  downloadAnchorNode.click();
  downloadAnchorNode.remove();
}

@Injectable({
  providedIn: 'root',
})
export class PackageDesignerEffects {
  openComponentsModal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(openComponentsModal),
      withLatestFrom(this.store$),
      map(([{ componentId }, store]) => {
        const component = (store.packageDesigner.package.components || []).find(findComponentById(componentId));
        if (!component) {
          return { type: 'Empty' };
        }
        const fixedComponent = fixComponentData(component);
        return setComponent({
          rawComponent: { ...fixedComponent.defaults, ...getDataFromComponent(component) } as any,
          component: fixedComponent,
        });
      }),
    ),
  );

  exportJSON$ = createEffect(() =>
    this.actions$.pipe(
      ofType(exportJSON),
      withLatestFrom(this.store$),
      mergeMap(([_, store]) =>
        this.packagesResource.export(store.packageDesigner.package.id).pipe(
          map((res: any) => {
            const fileName = store.packageDesigner.package.name.toLowerCase().replace(/\s/g, '_');
            downloadObjectAsJson(res, fileName);
            return exportJSONSuccess();
          }),
          catchError((err) => of(exportJSONError({ err }))),
        ),
      ),
    ),
  );

  importJSON$ = createEffect(() =>
    this.actions$.pipe(
      ofType(importJSON),
      withLatestFrom(this.store$),
      mergeMap(([{ body }, store]) => {
        let json: any;
        try {
          json = JSON.parse(body);
        } catch (e) {
          this.notifyService.error(this.translate.instant('response.422.invalid_json_message'));
          return of(importJSONError({ err: e }));
        }

        const { variables, metadata, components, edges } = json;
        const data_flow_json: any = { components, edges };
        const name = json.metadata && json.metadata.name ? json.metadata.name : 'Untitled package';
        const description = json.metadata && json.metadata.description ? json.metadata.description : '';

        return this.packagesResource
          .update(
            store.packageDesigner.package.id,
            {
              data_flow_json,
              variables,
              name,
              description,
              metadata,
            },
            { include: 'flow', auto_map_connections: true } as any,
          )
          .pipe(
            map((res) => {
              this.notifyService.success(
                this.translate.instant('designer.controls.actions.import_json_package.success'),
              );
              return setPackageFromJson({ packageItem: res });
            }),
            catchError((err) => of(exportJSONError({ err }))),
          );
      }),
    ),
  );

  setPackageFromJson$ = createEffect(() =>
    this.actions$.pipe(ofType(setPackageFromJson), map(closePackageImportModal)),
  );

  setPackageFromJson2$ = createEffect(() =>
    this.actions$.pipe(ofType(setPackageFromJson), map(closePackageVersionMessageModal)),
  );

  setPackageFromJson3$ = createEffect(() => this.actions$.pipe(ofType(setPackageFromJson), map(clearHistory)));

  createMajorVersion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createMajorVersion),
      withLatestFrom(this.store$),
      mergeMap(([{ message }, store]) => {
        const packageItem = cloneDeep(store.packageDesigner.package);
        packageItem.version_description = message;

        return this.packagesResource.majorVersion(store.packageDesigner.package.id, packageItem).pipe(
          map((res) => {
            this.notifyService.success(
              this.translate.instant('designer.controls.actions.create_major_version.success', {
                version: res.package_version,
              }),
            );
            return setPackageFromJson({ packageItem: { ...packageItem, ...res } });
          }),
          catchError((err) => of(createMajorVersionError({ err }))),
        );
      }),
    ),
  );

  savePackage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(savePackage),
      withLatestFrom(this.store$),
      mergeMap(([{ openJobModal, runValidation }, store]) => {
        const packageItem = cloneDeep(store.packageDesigner.package);
        packageItem.data_flow_json = JSON.stringify({
          components: store.packageDesigner.package.components,
          edges: store.packageDesigner.package.edges,
        });

        return this.packagesResource.update(store.packageDesigner.package.id, packageItem).pipe(
          map((res) => {
            this.notifyService.success(this.translate.instant('designer.controls.actions.save_package.success'));
            return savePackageSuccess({ openJobModal, runValidation });
          }),
          catchError((err) => of(savePackageError({ err }))),
        );
      }),
    ),
  );

  savePackageSuccess$ = createEffect(() => this.actions$.pipe(ofType(savePackageSuccess), map(clearHistory)));
  savePackageSuccessOpenModal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(savePackageSuccess),
      map(({ openJobModal }) => (openJobModal ? openJobsModal() : { type: 'empty' })),
    ),
  );
  savePackageSuccessValidation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(savePackageSuccess),
      map(({ runValidation }) => (runValidation ? runValidationAction() : { type: 'empty' })),
    ),
  );

  runValidationAction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runValidationAction),
      withLatestFrom(this.store$),
      mergeMap(([_, store]) => {
        return this.packagesResource.validate(store.packageDesigner.package.id).pipe(
          map((res) => {
            return getValidationStatus({ id: res.id });
          }),
          catchError((err) => of(runValidationError({ err }))),
        );
      }),
    ),
  );

  pollValidationStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getValidationStatus),
      withLatestFrom(this.store$),
      mergeMap(([{ id }, store]) =>
        this.startPolling(id, store.packageDesigner.package.id).pipe(
          takeWhile((res) => res.status === 'running', true),
          map((res: any) => {
            if (res.status === 'running') {
              return { type: 'empty' };
            }
            return res.status === 'completed' ? validationStatusSuccess() : validationStatusError({ err: res.errors });
          }),
          catchError((err) => of(validationStatusError({ err }))),
        ),
      ),
    ),
  );

  cutAction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cutAction),
      map(() => removeComponents({})),
    ),
  );

  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private packagesResource: PackagesResource,
    private notifyService: NotifyService,
    private translate: TranslateService,
  ) {}

  private startPolling(validation_id: number, package_id: number, interval: number = 3000): Observable<any> {
    return timer(0, interval).pipe(switchMap(() => this.packagesResource.validateStatus(package_id, validation_id)));
  }
}
